import { useEffect, useRef } from 'react';
import { Auth } from 'aws-amplify';
import { confirmDialog } from 'primereact/confirmdialog';
import Cookies from 'universal-cookie';
import { logout } from 'utils/utils';

const cookies = new Cookies();

/**
 * Checks whether the AWS Amplify/Cognito session is still valid each time the
 * browser tab regains focus (visibilitychange → visible).  When the session has
 * expired the hook shows a "Session Expired" confirmation dialog; clicking
 * "Login Again" clears all cookies/local-storage and redirects to login.
 *
 * @param {{ history?: import('history').History }} options
 */
export default function useSessionCheck({ history = null } = {}) {
  const isCheckingRef = useRef(false);
  const dialogShownRef = useRef(false);

  const handleSessionExpired = () => {
    if (dialogShownRef.current) return;
    dialogShownRef.current = true;

    confirmDialog({
      message: 'Your session has expired. Please log in again to continue.',
      header: 'Session Expired',
      icon: 'pi pi-exclamation-circle',
      acceptLabel: 'Login Again',
      rejectLabel: '',
      acceptClassName: 'p-button-primary',
      rejectClassName: 'p-button-secondary d-none',
      closable: false,
      footerClassName: 'd-flex justify-content-center',
      style: { width: 420 },
      accept: () => {
        dialogShownRef.current = false;
        logout(history);
      },
      reject: () => null,
    });
  };

  const checkSession = async () => {
    const token = cookies.get('token');
    if (!token || isCheckingRef.current || dialogShownRef.current) return;

    isCheckingRef.current = true;
    try {
      await Auth.currentSession();
    } catch {
      handleSessionExpired();
    } finally {
      isCheckingRef.current = false;
    }
  };

  useEffect(() => {
    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        checkSession();
      }
    };

    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => document.removeEventListener('visibilitychange', onVisibilityChange);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
}
